import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { clearSignupSource, getSignupSource } from "./signupSource";

const AuthCtx = createContext({
  user: null,
  loading: true,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  refresh: async () => {},
  setUser: () => {},
  authError: false,
  retryAuth: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // True when /auth/me failed for a reason other than "you are not signed in"
  // (network down, timeout, a 5xx during a cold start). We don't know whether
  // the session is valid, so we neither sign the user out nor pretend they are.
  const [authError, setAuthError] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      setAuthError(false);
    } catch (err) {
      if (err?.response?.status === 401) {
        // The server says this session is not valid: that is a real sign-out.
        localStorage.removeItem("gumi_token");
        setUser(null);
        setAuthError(false);
      } else {
        // Keep whoever is already signed in; if nobody is yet, flag it so
        // ProtectedRoute offers a retry instead of bouncing to /login.
        setAuthError(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const retryAuth = useCallback(async () => {
    setLoading(true);
    await refresh();
  }, [refresh]);

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.location.pathname === "/auth/callback" &&
      (window.location.search.includes("token=") || window.location.hash.includes("token="))
    ) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("gumi_token", r.data.access_token);
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (email, password, name) => {
    const r = await api.post("/auth/register", { email, password, name, signup_source: getSignupSource() });
    clearSignupSource();
    localStorage.setItem("gumi_token", r.data.access_token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("gumi_token");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh, setUser, authError, retryAuth }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
